import { type PayChangeRequest, type PerformanceReview, type User } from '@kalos/kalos-rpc';
import { addDays, format, parseISO } from 'date-fns';

import { NULL_TIME } from '../../../../constants';

/** The statuses a pay change request can have, in the order desktop's filter lists them. */
export const PAY_CHANGE_STATUSES = ['Pending', 'Canceled', 'Rejected', 'Approved'] as const;
export type PayChangeStatus = (typeof PAY_CHANGE_STATUSES)[number];

/** The status as a tinted pill: waiting, done, or a plain past state. Never red. */
export const payChangeStatusTone = (status: string) =>
  status === 'Approved' ? 'success' : status === 'Pending' ? 'warning' : 'neutral';

/** "$1.50", as every desktop table shows the amount. */
export const payChangeAmountLabel = (amount: number) => `$${amount.toFixed(2)}`;

/** "Oct 3, 2026, 2:15 PM"; nothing for a request that hasn't been reviewed. */
export const payChangeDateTimeLabel = (value: string) =>
  !value || value === NULL_TIME ? '' : format(parseISO(value), 'MMM d, yyyy, h:mm a');

/** "Oct 3, 2026" for a row. */
export const payChangeDateLabel = (value: string) =>
  !value || value === NULL_TIME ? '' : format(parseISO(value), 'MMM d, yyyy');

/** "First Last" from the employee list; nothing when the id is 0 or the person isn't in it. */
export const payChangePersonName = (
  users: Pick<User, 'id' | 'firstname' | 'lastname'>[],
  id: number,
) => {
  const user = id === 0 ? undefined : users.find((candidate) => candidate.id === id);
  return user ? `${user.firstname} ${user.lastname}` : '';
};

/**
 * Which requests a viewer may see (desktop's table in the evaluation form): an approver sees
 * every request; anyone else only the pending and approved ones, never rejected or canceled.
 */
export const visiblePayChangeRequests = (requests: PayChangeRequest[], isApprover: boolean) =>
  isApprover
    ? requests
    : requests.filter(
        (request) => request.requestStatus === 'Pending' || request.requestStatus === 'Approved',
      );

/** The request waiting for a decision, if any. */
export const pendingPayChangeRequest = (requests: PayChangeRequest[]) =>
  requests.find((request) => request.requestStatus === 'Pending');

/** Requests already decided or withdrawn, newest first. */
export const pastPayChangeRequests = (requests: PayChangeRequest[]) =>
  requests
    .filter((request) => request.requestStatus !== 'Pending')
    .sort(
      (first, second) =>
        parseISO(second.dateSubmitted).getTime() - parseISO(first.dateSubmitted).getTime(),
    );

/**
 * Desktop's "View Pay Change Requests" on the review: only an approver, and only when the review
 * has at least one request.
 */
export const canSeeReviewPayChangeSection = ({
  isApprover,
  requests,
}: {
  isApprover: boolean;
  requests: PayChangeRequest[];
}) => isApprover && requests.length > 0;

/**
 * What the manager writing the evaluation can do about a pay change (desktop's Amount Change
 * block): request one while the review is unlocked and has none approved; cancel their own
 * pending request. Someone else's pending request, or an approved one, leaves nothing to do.
 */
export const payChangeRequestAbility = ({
  review,
  requests,
  viewerId,
  canEditManagerEvaluation,
}: {
  review: Pick<PerformanceReview, 'lockReview' | 'includesPaychange'>;
  requests: PayChangeRequest[];
  viewerId: number;
  canEditManagerEvaluation: boolean;
}): { canSubmit: boolean; canCancel: boolean; reason: string } => {
  const pending = pendingPayChangeRequest(requests);
  if (review.includesPaychange) {
    return {
      canSubmit: false,
      canCancel: false,
      reason: 'A pay change has already been approved for this review.',
    };
  }
  if (review.lockReview) {
    if (pending && pending.submittedBy === viewerId) {
      return {
        canSubmit: false,
        canCancel: true,
        reason: 'Your request is waiting for approval. You can cancel it until it is decided.',
      };
    }
    return {
      canSubmit: false,
      canCancel: false,
      reason: 'A pay change request is already waiting for approval on this review.',
    };
  }
  if (!canEditManagerEvaluation) {
    return {
      canSubmit: false,
      canCancel: false,
      reason: 'Only a manager who is writing this evaluation can request a pay change.',
    };
  }
  return { canSubmit: true, canCancel: false, reason: '' };
};

/**
 * A pay change amount typed on a phone: a positive dollar figure. Desktop takes whatever number
 * the text parses to, including 0 when the field was never left.
 */
export const parsePayChangeAmount = (
  text: string,
): { amount: number; error: '' } | { amount: null; error: string } => {
  const trimmed = text.trim();
  if (trimmed === '') return { amount: null, error: 'Enter the amount of the change.' };
  const amount = Number(trimmed);
  if (!Number.isFinite(amount)) return { amount: null, error: 'Enter the amount as a number.' };
  if (amount <= 0) return { amount: null, error: 'Enter an amount greater than $0.00.' };
  return { amount, error: '' };
};

/** Desktop's review name column: the review's text, with the due day for a supplementary review. */
export const payChangeReviewName = (
  review: Pick<PerformanceReview, 'text' | 'reviewType' | 'scheduleDate'> | undefined,
) =>
  review
    ? `${review.text}${review.reviewType === 0 ? ` - ${format(parseISO(review.scheduleDate), 'MM/dd')}` : ''}`
    : '';

export type PayChangeListFilters = {
  /** One box: the review's name or the employee's name. */
  search: string;
  /** yyyy-MM-dd */
  dateFrom: string;
  /** yyyy-MM-dd */
  dateTo: string;
  /** Desktop's "Ignore Date Range". */
  anyDate: boolean;
  /** None chosen means every status, as on desktop. */
  statuses: PayChangeStatus[];
};

/** Desktop's default: submitted in the last 30 days, any status. */
export const defaultPayChangeListFilters = (today: Date): PayChangeListFilters => ({
  search: '',
  dateFrom: format(addDays(today, -30), 'yyyy-MM-dd'),
  dateTo: format(today, 'yyyy-MM-dd'),
  anyDate: false,
  statuses: [],
});

/**
 * The list desktop loads for the pay change view: requests on active reviews only, newest
 * submitted first.
 */
export const payChangeRequestsOnActiveReviews = (
  requests: PayChangeRequest[],
  reviews: Pick<PerformanceReview, 'id' | 'isActive'>[],
) => {
  const activeReviewIds = new Set(
    reviews.filter((review) => review.isActive === 1).map((review) => review.id),
  );
  return requests
    .filter((request) => activeReviewIds.has(request.prId))
    .sort(
      (first, second) =>
        parseISO(second.dateSubmitted).getTime() - parseISO(first.dateSubmitted).getTime(),
    );
};

/**
 * Desktop's pay change filter (`onPayChangeFormSave`), with its three text boxes as one search.
 * As on desktop: the date range runs from after midnight on the first day to the end of the
 * last, a request whose employee isn't in the employee list is left out, and no chosen status
 * means every status.
 */
export const filterPayChangeRequests = ({
  requests,
  filters,
  employees,
  reviews,
}: {
  requests: PayChangeRequest[];
  filters: PayChangeListFilters;
  employees: Pick<User, 'id' | 'firstname' | 'lastname'>[];
  reviews: Pick<PerformanceReview, 'id' | 'text'>[];
}) => {
  const search = filters.search.trim().toLowerCase();
  return requests.filter((request) => {
    if (
      !filters.anyDate &&
      (parseISO(filters.dateFrom) >= parseISO(request.dateSubmitted) ||
        addDays(parseISO(filters.dateTo), 1) <= parseISO(request.dateSubmitted))
    ) {
      return false;
    }
    const employee = employees.find((user) => user.id === request.empId);
    if (!employee) return false;
    if (search) {
      const employeeName = `${employee.firstname} ${employee.lastname}`.toLowerCase();
      const reviewName =
        reviews.find((review) => review.id === request.prId)?.text.toLowerCase() ?? '';
      if (!employeeName.includes(search) && !reviewName.includes(search)) return false;
    }
    if (
      filters.statuses.length > 0 &&
      !(filters.statuses as string[]).includes(request.requestStatus)
    ) {
      return false;
    }
    return true;
  });
};
